/**
 * Convert raw Firebase / backend error messages into user-friendly text.
 * Usage:  setError(friendlyError(err))
 */

const FIREBASE_MAP = {
  'auth/wrong-password':            'Incorrect password. Please try again.',
  'auth/invalid-credential':        'Invalid email or password. Please check and try again.',
  'auth/user-not-found':            'No account found with this email address.',
  'auth/invalid-email':             'Please enter a valid email address.',
  'auth/user-disabled':             'This account has been disabled. Contact your administrator.',
  'auth/too-many-requests':         'Too many failed attempts. Please wait a moment and try again.',
  'auth/network-request-failed':    'Network error. Check your internet connection and try again.',
  'auth/email-already-in-use':      'This email is already registered.',
  'auth/weak-password':             'Password is too weak. Use at least 6 characters.',
  'auth/requires-recent-login':     'Session expired. Please sign in again.',
  'auth/popup-closed-by-user':      'Sign-in was cancelled.',
  'auth/internal-error':            'An unexpected error occurred. Please try again.',
  'auth/missing-password':          'Please enter your password.',
  'auth/missing-email':             'Please enter your email address.',
};

const GENERIC_PATTERNS = [
  [/failed to fetch|networkerror|fetch failed/i,     'Unable to reach the server. Please check your connection.'],
  [/timeout|timed out/i,                              'Request timed out. Please try again.'],
  [/401|unauthorized/i,                               'Session expired. Please sign in again.'],
  [/invalid or expired token|token expired|expired token/i, 'Session expired. Please sign in again.'],
  [/403|forbidden/i,                                  'You do not have permission for this action.'],
  [/404|not found/i,                                  'The requested resource was not found.'],
  [/500|internal server/i,                            'Something went wrong on the server. Please try again later.'],
  [/cors|cross-origin/i,                              'Unable to reach the server. Please try again.'],
];

export const friendlyError = (err, fallback) => {
  if (!err) return fallback || 'Something went wrong. Please try again.';

  // Firebase errors carry a code property
  const code = err?.code || '';
  if (code && FIREBASE_MAP[code]) {
    return FIREBASE_MAP[code];
  }

  // Firebase sometimes embeds the code in the message string like "Firebase: Error (auth/wrong-password)."
  const msg = err?.message || (typeof err === 'string' ? err : '');
  for (const [firebaseCode, friendly] of Object.entries(FIREBASE_MAP)) {
    if (msg.includes(firebaseCode)) {
      return friendly;
    }
  }

  // Match generic patterns
  for (const [pattern, friendly] of GENERIC_PATTERNS) {
    if (pattern.test(msg)) {
      return friendly;
    }
  }

  // If the message looks technical (contains firebase, stack traces, etc.), return generic
  if (/firebase|firestore|grpc|at\s+\w+\s+\(|Error\s*\(/i.test(msg)) {
    return fallback || 'Something went wrong. Please try again.';
  }

  // Otherwise the message is already readable (e.g. from our own backend)
  return msg || fallback || 'Something went wrong. Please try again.';
};
